/**
 * Tabs (D5) nach dem ARIA-Tabs-Muster.
 * Markup ohne JS: alle Panels untereinander, jedes mit eigener Überschrift.
 *   <div class="c-tabs" data-tabs>
 *     <section class="c-tabs__panel" id="…">
 *       <h3 class="c-tabs__panel-title">Links</h3>
 *       <div class="c-tabs__content">…</div>
 *     </section>
 *   </div>
 * Ab 48em: Pillen-Leiste. Darunter: Akkordeon (gleiche Panels, Titel als Button).
 */
const MQ = window.matchMedia('(min-width: 48em)');
let uid = 0;

export function initTabs() {
  document.querySelectorAll('[data-tabs]').forEach(setup);
}

function setup(root) {
  const panels = [...root.querySelectorAll(':scope > .c-tabs__panel')];
  if (!panels.length) return;
  const n = ++uid;

  /* Tab-Leiste aufbauen */
  const list = document.createElement('div');
  list.className = 'c-tabs__list';
  list.setAttribute('role', 'tablist');
  if (root.dataset.label) list.setAttribute('aria-label', root.dataset.label);

  const tabs = panels.map((panel, i) => {
    panel.id ||= `tabs-${n}-panel-${i}`;
    const title = panel.querySelector('.c-tabs__panel-title');
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'c-tabs__tab';
    tab.id = `${panel.id}-tab`;
    tab.textContent = title.textContent.trim();
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', panel.id);
    list.append(tab);

    /* Akkordeon-Schalter für die schmale Ansicht */
    const accBtn = document.createElement('button');
    accBtn.type = 'button';
    accBtn.className = 'c-tabs__acc-button';
    accBtn.setAttribute('aria-expanded', 'false');
    accBtn.setAttribute('aria-controls', `${panel.id}-content`);
    accBtn.innerHTML = `<span>${title.innerHTML}</span><span class="c-accordion__toggle" aria-hidden="true"></span>`;
    title.dataset.text = title.innerHTML;
    const content = panel.querySelector('.c-tabs__content');
    content.id = `${panel.id}-content`;
    accBtn.addEventListener('click', () => {
      const open = accBtn.getAttribute('aria-expanded') !== 'true';
      accBtn.setAttribute('aria-expanded', String(open));
      content.hidden = !open;
    });
    panel._acc = { title, accBtn, content };
    return tab;
  });
  root.prepend(list);

  function select(index, focus = false) {
    tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      if (MQ.matches) panels[i].hidden = !on;
    });
    if (focus) tabs[index].focus();
    root._selected = index;
  }

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(i));
    tab.addEventListener('keydown', (e) => {
      const last = tabs.length - 1;
      const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: last }[e.key];
      if (next === undefined) return;
      e.preventDefault();
      select((next + tabs.length) % tabs.length, true);
    });
  });

  function applyMode() {
    const wide = MQ.matches;
    root.classList.toggle('is-enhanced', wide);
    root.classList.toggle('is-accordion', !wide);
    panels.forEach((panel, i) => {
      const { title, accBtn, content } = panel._acc;
      if (wide) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabs[i].id);
        panel.tabIndex = 0;
        if (accBtn.isConnected) { accBtn.remove(); title.innerHTML = title.dataset.text; }
        content.hidden = false;
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
        panel.hidden = false;
        if (!accBtn.isConnected) { title.textContent = ''; title.append(accBtn); }
        const open = accBtn.getAttribute('aria-expanded') === 'true';
        content.hidden = !open;
      }
    });
    select(root._selected ?? 0);
  }
  applyMode();
  MQ.addEventListener('change', applyMode);

  /* Für Deep-Links: Panel mit Ziel auswählen bzw. aufklappen */
  root.addEventListener('tabs:reveal', (e) => {
    const index = panels.findIndex((p) => p.contains(e.detail));
    if (index < 0) return;
    if (MQ.matches) select(index);
    else {
      const { accBtn, content } = panels[index]._acc;
      accBtn.setAttribute('aria-expanded', 'true');
      content.hidden = false;
    }
  });
}
